import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Bot, LayoutGrid, Database, Users, Layers, ArrowLeft, Plus, Copy, Pencil, Trash2,
  Eye, Pause, Play, KeyRound, CheckCircle2, XCircle, Search, RefreshCw, Film, Tv, Archive, Download,
} from "lucide-react";
import { api, formatApiError, fmtBytes } from "../lib/api";
import { GlassCard, Modal, Spinner, EmptyState, QualityBadge, VersionBadge } from "../components/common";
import { TutorialHint } from "../components/TutorialHint";

const TABS = [
  { id: "overview", label: "Visão geral", icon: LayoutGrid },
  { id: "storage", label: "Storage", icon: Database },
  { id: "accesses", label: "Acessos", icon: Users },
  { id: "acervo", label: "Acervo", icon: Layers },
];

const KIND_OPTS = [["movie", "Filmes"], ["tv", "Séries"]];

export default function BotDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [ov, setOv] = useState(null);
  const [acesso, setAcervo] = useState(null);
  const [accesses, setAccesses] = useState(null);

  const [cfg, setCfg] = useState({ storage_chat_id: "", admin_ids: "" });
  const [savingCfg, setSavingCfg] = useState(false);
  const [linkTid, setLinkTid] = useState("");
  const [linking, setLinking] = useState(false);

  const [newAcc, setNewAcc] = useState({ name: "", username: "", password: "", max_concurrent: 1 });
  const [creatingAcc, setCreatingAcc] = useState(false);
  const [creds, setCreds] = useState({});
  const [linkQ, setLinkQ] = useState({});
  const [linkItems, setLinkItems] = useState({});
  const [playing, setPlaying] = useState(null);
  const [editLimit, setEditLimit] = useState({});
  const [editPass, setEditPass] = useState({});

  const [filters, setFilters] = useState({ kind: "", archived: "false", q: "", category: "" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [catLimit, setCatLimit] = useState(10);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkFilterOpen, setBulkFilterOpen] = useState(false);
  const [bulkForm, setBulkForm] = useState({ category: "", min_size_mb: "", kind: "", action: "archive" });
  const [confirmState, setConfirmState] = useState(null);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [newCat, setNewCat] = useState({ name: "", kind: "movie" });
  const [renaming, setRenaming] = useState(null);
  const [renameVal, setRenameVal] = useState("");

  const loadOverview = useCallback(() => {
    api.get(`/instances/${id}/overview`).then((r) => {
      setOv(r.data);
      setCfg({ storage_chat_id: r.data.bot.storage_chat_id || "", admin_ids: r.data.bot.admin_ids || "", s3_prefix: r.data.bot.s3_prefix || "vod" });
    }).catch((e) => { toast.error(formatApiError(e)); navigate("/app/bots"); });
  }, [id, navigate]);

  const loadAccesses = useCallback(() => {
    api.get(`/instances/${id}/accesses`).then((r) => setAccesses(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id]);

  const loadAcervo = useCallback((f = filters, p = page, ps = pageSize) => {
    api.get(`/instances/${id}/acervo`, {
      params: { kind: f.kind || undefined, archived: f.archived, q: f.q || undefined, category: f.category || undefined, skip: p * ps, limit: ps },
    }).then((r) => setAcervo(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [id, filters, page, pageSize]);

  useEffect(() => { loadOverview(); }, [loadOverview]);
  useEffect(() => { if (tab === "accesses") loadAccesses(); }, [tab, loadAccesses]);
  useEffect(() => {
    if (tab !== "accesses") return;
    const t = setInterval(loadAccesses, 15000);
    return () => clearInterval(t);
  }, [tab, loadAccesses]);
  useEffect(() => { if (tab === "acervo") { loadAcervo(); loadAccesses(); } }, [tab]);

  const copy = (text, msg = "Copiado!") => navigator.clipboard.writeText(text).then(() => toast.success(msg));

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      await api.post(`/bots/${id}/config`, cfg);
      toast.success("Configurações salvas");
      loadOverview();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingCfg(false); }
  };

  const reactivate = async () => {
    try {
      await api.post(`/bots/${id}/webhook`);
      toast.success("Webhook reativado");
      loadOverview();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const linkTelegram = async () => {
    setLinking(true);
    try {
      await api.post(`/bots/${id}/link-telegram`, { telegram_id: linkTid.trim() });
      toast.success("Telegram vinculado! Abra o bot e envie /start");
      setLinkTid("");
    } catch (e) { toast.error(formatApiError(e)); } finally { setLinking(false); }
  };

  const createAccess = async () => {
    setCreatingAcc(true);
    try {
      await api.post(`/instances/${id}/accesses`, { ...newAcc, max_concurrent: parseInt(newAcc.max_concurrent) || 0 });
      toast.success("Acesso criado");
      setNewAcc({ name: "", username: "", password: "", max_concurrent: 1 });
      loadAccesses();
      loadOverview();
    } catch (e) { toast.error(formatApiError(e)); } finally { setCreatingAcc(false); }
  };

  const toggleAccess = async (accId) => {
    try { await api.post(`/instances/accesses/${accId}/toggle`); loadAccesses(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeAccess = async (accId) => {
    try { await api.delete(`/instances/accesses/${accId}`); toast.success("Acesso excluído"); loadAccesses(); loadOverview(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyLimit = async (accId) => {
    try {
      await api.post(`/instances/accesses/${accId}/limit`, { max_concurrent: parseInt(editLimit[accId]) || 0 });
      toast.success("Limite atualizado");
      setEditLimit({ ...editLimit, [accId]: undefined });
      loadAccesses();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyPassword = async (accId) => {
    try {
      const { data } = await api.post(`/instances/accesses/${accId}/password`, { password: editPass[accId] || null });
      toast.success(`Nova senha: ${data.password}`);
      setEditPass({ ...editPass, [accId]: undefined });
      loadAccesses();
      setCreds((c) => { const n = { ...c }; delete n[accId]; return n; });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadCredLinks = async (accId, q = "") => {
    try {
      const r = await api.get(`/instances/${id}/acervo`, { params: { archived: "false", ...(q ? { q } : {}) } });
      setLinkItems((prev) => ({ ...prev, [accId]: { items: r.data.items, total: r.data.total } }));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const viewCreds = async (accId) => {
    if (creds[accId]) { setCreds((c) => { const n = { ...c }; delete n[accId]; return n; }); return; }
    try {
      const { data } = await api.get(`/instances/accesses/${accId}/credentials`);
      setCreds((c) => ({ ...c, [accId]: data }));
      await loadCredLinks(accId);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyFilters = (patch) => {
    const f = { ...filters, ...patch };
    setFilters(f);
    setPage(0);
    loadAcervo(f, 0, pageSize);
  };

  const goPage = (delta) => {
    const p = page + delta;
    setPage(p);
    loadAcervo(filters, p, pageSize);
  };

  const changePageSize = (n) => {
    setPageSize(n);
    setPage(0);
    loadAcervo(filters, 0, n);
  };

  const toggleSelect = (vid) => setSelected((prev) => {
    const n = new Set(prev);
    if (n.has(vid)) n.delete(vid); else n.add(vid);
    return n;
  });
  const allSelected = !!(acesso && acesso.items.length > 0 && acesso.items.every((i) => selected.has(i.version_id)));
  const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(acesso.items.map((i) => i.version_id)));

  const runBulk = async (action, ids) => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/instances/${id}/versions/bulk`, { action, version_ids: ids });
      const freed = data.freed_bytes > 0 ? ` — ${fmtBytes(data.freed_bytes)} liberados` : "";
      const verb = action === "delete" ? "excluído(s) definitivamente" : action === "archive" ? "movido(s) para a lixeira" : "restaurado(s)";
      toast.success(`${data.affected} item(ns) ${verb}${freed}`);
      setSelected(new Set());
      loadAcervo();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
      setConfirmState(null);
    }
  };

  const runBulkFilter = async () => {
    setBulkBusy(true);
    try {
      const payload = { action: bulkForm.action };
      if (bulkForm.category) payload.category = bulkForm.category;
      if (bulkForm.min_size_mb) payload.min_size_mb = Number(bulkForm.min_size_mb);
      if (bulkForm.kind) payload.kind = bulkForm.kind;
      if (filters.q) payload.q = filters.q;
      const { data } = await api.post(`/instances/${id}/versions/bulk-by-filter`, payload);
      const freed = data.freed_bytes > 0 ? ` — ${fmtBytes(data.freed_bytes)} liberados` : "";
      toast.success(data.affected ? `${data.affected} item(ns) ${bulkForm.action === "delete" ? "excluídos definitivamente" : "movidos para a lixeira"}${freed}` : "Nenhum item encontrado com esse filtro");
      setBulkFilterOpen(false);
      setSelected(new Set());
      loadAcervo();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const emptyTrash = async () => {
    setBulkBusy(true);
    try {
      const { data } = await api.post(`/instances/${id}/trash/empty`);
      toast.success(data.affected ? `Lixeira esvaziada: ${data.affected} item(ns) removidos — ${fmtBytes(data.freed_bytes)} liberados` : "Lixeira já está vazia");
      loadAcervo();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
      setConfirmState(null);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({ title: item.title, category: item.category || "", version: item.version, quality: item.quality, archived: item.archived, year: item.year || "" });
  };

  const saveEdit = async () => {
    try {
      const payload = { ...editForm };
      payload.year = payload.year ? parseInt(payload.year) : null;
      await api.put(`/instances/${id}/versions/${editItem.version_id}`, payload);
      toast.success("Item atualizado");
      setEditItem(null);
      loadAcervo();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeVersion = async (vid) => {
    try { await api.delete(`/instances/${id}/versions/${vid}`); toast.success("Item excluído"); loadAcervo(); loadOverview(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const createCategory = async () => {
    if (!newCat.name.trim()) return;
    try {
      await api.post(`/instances/${id}/categories`, newCat);
      toast.success("Categoria criada");
      setNewCat({ name: "", kind: "movie" });
      loadAcervo();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const renameCategory = async (name) => {
    if (!renameVal.trim()) return;
    try {
      await api.put(`/instances/${id}/categories/${encodeURIComponent(name)}`, { new_name: renameVal.trim() });
      toast.success("Categoria renomeada");
      setRenaming(null);
      loadAcervo();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteCategory = async (name) => {
    try {
      await api.delete(`/instances/${id}/categories/${encodeURIComponent(name)}`);
      toast.success("Categoria removida");
      loadAcervo();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const firstAccess = (accesses || []).find((a) => a.status === "active");
  const itemLink = (item) => {
    if (!firstAccess) return null;
    const kindPath = item.media_type === "tv" ? "series" : "movie";
    return `${window.location.origin}/api/xc/${kindPath}/${firstAccess.username}/${firstAccess.password}/${item.version_id}.mp4`;
  };

  if (!ov) return <div className="flex justify-center py-24"><Spinner /></div>;

  return (
    <div className="space-y-6" data-testid="bot-detail-page">
      <button onClick={() => navigate("/app/bots")} className="text-xs text-slate-500 hover:text-cyan-300 transition-colors inline-flex items-center gap-1.5" data-testid="back-to-bots">
        <ArrowLeft size={13} /> Central de Instâncias
      </button>

      <GlassCard className="p-6 anim-fade-up" data-testid="bot-detail-header">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-2xl bg-emerald-400/10 border border-emerald-400/25 flex items-center justify-center shrink-0">
            <Bot size={22} className="text-emerald-400" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="font-display text-xl font-bold text-white font-mono2">@{ov.bot.username}</h1>
              <span className={`px-2 py-0.5 rounded-md text-[10px] font-mono2 font-semibold uppercase tracking-wider border ${ov.online ? "bg-emerald-400/10 border-emerald-400/40 text-emerald-300" : "bg-rose-400/10 border-rose-400/40 text-rose-300"}`} data-testid="bot-detail-status">
                {ov.online ? "Ativo" : "Offline"}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-1">{ov.bot.name} · gestão isolada por instância</p>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-6 p-1.5 rounded-2xl bg-slate-950/50 border border-slate-800/60">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-semibold transition-colors ${
                tab === t.id ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/30" : "text-slate-400 hover:text-white border border-transparent"
              }`}
            >
              <t.icon size={14} />
              {t.label}
              {t.id === "accesses" && <span className="text-[10px] font-mono2 opacity-70">{ov.acessos}</span>}
              {t.id === "acervo" && <span className="text-[10px] font-mono2 opacity-70">{ov.arquivos}</span>}
            </button>
          ))}
        </div>
      </GlassCard>

      {tab === "overview" && (
        <div className="space-y-5 anim-fade-up" data-testid="tab-content-overview">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: "Storage", value: ov.storage_title || (ov.bot.storage_chat_id ? "Configurado" : "Não definido"), icon: Database, accent: "text-cyan-400", testid: "ov-storage" },
              { label: "Arquivos", value: ov.arquivos, icon: Layers, accent: "text-purple-400", testid: "ov-arquivos" },
              { label: "Acessos", value: ov.acessos, icon: Users, accent: "text-amber-400", testid: "ov-acessos" },
              { label: "Espaço utilizado", value: fmtBytes(ov.espaco_bytes), icon: Database, accent: "text-emerald-400", testid: "ov-espaco" },
            ].map((c) => (
              <GlassCard key={c.label} className="p-5 glass-hover" data-testid={c.testid}>
                <c.icon size={17} className={c.accent} />
                <p className="font-display text-lg font-bold text-white mt-2 truncate">{c.value}</p>
                <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 mt-1">{c.label}</p>
              </GlassCard>
            ))}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            <GlassCard className="p-6" data-testid="estado-instancia-card">
              <h3 className="font-display text-white font-semibold text-sm mb-1.5">Estado da instância</h3>
              <p className="text-xs text-slate-500 mb-4">Acompanhe rapidamente as etapas necessárias para operar este bot.</p>
              <div className="space-y-2.5">
                {[
                  ["Bot conectado", ov.checklist.bot_conectado],
                  ["Storage definido", ov.checklist.storage_definido],
                  ["Telegram vinculado", ov.checklist.telegram_vinculado],
                ].map(([label, ok]) => (
                  <div key={label} className={`flex items-center gap-2.5 p-3 rounded-xl border text-sm ${ok ? "bg-emerald-400/5 border-emerald-400/25 text-emerald-200" : "bg-amber-400/5 border-amber-400/25 text-amber-200"}`}>
                    {ok ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
                    {label}
                    <span className="ml-auto text-[10px] font-mono2 uppercase">{ok ? "Concluído" : "Pendente"}</span>
                  </div>
                ))}
              </div>
            </GlassCard>
            <GlassCard className="p-6" data-testid="atalhos-card">
              <h3 className="font-display text-white font-semibold text-sm mb-1.5">Atalhos de configuração</h3>
              <p className="text-xs text-slate-500 mb-4">Continue exatamente de onde precisa.</p>
              <div className="space-y-2.5">
                <button data-testid="shortcut-storage" onClick={() => setTab("storage")} className="w-full btn-ghost-dark rounded-xl px-4 py-3.5 text-sm text-left text-slate-200 flex items-center gap-2.5">
                  <Database size={15} className="text-cyan-400" /> Gerenciar storage e Telegram
                </button>
                <button data-testid="shortcut-accesses" onClick={() => setTab("accesses")} className="w-full btn-ghost-dark rounded-xl px-4 py-3.5 text-sm text-left text-slate-200 flex items-center gap-2.5">
                  <Users size={15} className="text-purple-400" /> Gerenciar consumidores
                </button>
                <button data-testid="shortcut-acervo" onClick={() => setTab("acervo")} className="w-full btn-ghost-dark rounded-xl px-4 py-3.5 text-sm text-left text-slate-200 flex items-center gap-2.5">
                  <Layers size={15} className="text-emerald-400" /> Ver acervo e categorias
                </button>
              </div>
            </GlassCard>
          </div>
        </div>
      )}

      {tab === "storage" && (
        <div className="space-y-5 anim-fade-up" data-testid="tab-content-storage">
          <TutorialHint
            id="bot-storage"
            title="Storage em nuvem e conexão Telegram"
            steps={[
              "Os vídeos são guardados na nuvem privada da plataforma (configurada pelo admin em Ajustes → Armazenamento) — não é mais necessário grupo/canal do Telegram.",
              "Vincule seu Telegram no campo de administradores com seu ID de USUÁRIO (ex: 5914704792) — é ele que libera o menu do bot.",
              "Se o bot parar de responder, use 'Reconectar e atualizar'.",
            ]}
          />
          <GlassCard className="p-6" data-testid="storage-tab-card">
            <h3 className="font-display text-white font-semibold text-sm mb-4">Storage e conexão Telegram</h3>
            <div className={`flex items-center gap-2.5 p-3.5 rounded-xl border text-sm mb-5 ${ov.online ? "bg-emerald-400/5 border-emerald-400/25 text-emerald-200" : "bg-rose-400/5 border-rose-400/25 text-rose-300"}`} data-testid="bot-connected-status">
              {ov.online ? <CheckCircle2 size={15} /> : <XCircle size={15} />}
              {ov.online ? `Bot conectado — @${ov.bot.username} está recebendo atualizações do Telegram.` : "Bot offline — verifique o token."}
            </div>
            <div className="space-y-5">
              <div className="rounded-xl border border-cyan-400/25 bg-cyan-400/5 px-4 py-3.5" data-testid="storage-cloud-notice">
                <p className="text-xs text-cyan-200 leading-relaxed">
                  <b>Storage em nuvem.</b> Os vídeos sobem direto da origem para a nuvem privada da plataforma — sem ocupar grupos do Telegram. O streaming sai pelo domínio do painel, então o player nunca vê a origem.
                </p>
              </div>
              {ov?.bot?.is_admin_view && (
                <div>
                  <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Pasta no bucket (só admin)</label>
                  <input data-testid="storage-prefix-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" placeholder="vod" value={cfg.s3_prefix || ""} onChange={(e) => setCfg({ ...cfg, s3_prefix: e.target.value })} />
                  <p className="text-[11px] text-slate-600 mt-1.5">Os arquivos deste bot ficam na pasta <span className="font-mono2 text-cyan-300">{(cfg.s3_prefix || "vod").replace(/^\/+|\/+$/g, "") || "vod"}/</span> do bucket — use uma pasta por cliente (ex: cliente-joao).</p>
                </div>
              )}
              {ov?.bot?.can_write ? (
                <div>
                  <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Vincular seu Telegram (IDs de administradores)</label>
                  <input data-testid="storage-admins-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" placeholder="5914704792, 123456789" value={cfg.admin_ids} onChange={(e) => setCfg({ ...cfg, admin_ids: e.target.value })} />
                  <p className="text-[11px] text-slate-600 mt-1.5">Descubra seu ID com @userinfobot. Separe múltiplos IDs por vírgula.</p>
                </div>
              ) : (
                <div>
                  <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Vincular meu Telegram neste bot global</label>
                  <div className="flex gap-3">
                    <input data-testid="link-telegram-input" className="input-dark flex-1 rounded-xl px-4 py-3 text-sm font-mono2" placeholder="Seu ID de usuário (ex: 5914704792)" value={linkTid} onChange={(e) => setLinkTid(e.target.value)} />
                    <button data-testid="link-telegram-btn" onClick={linkTelegram} disabled={linking || !linkTid.trim()} className="btn-neon px-5 py-3 rounded-xl text-sm">
                      {linking ? <Spinner /> : "Vincular"}
                    </button>
                  </div>
                  <p className="text-[11px] text-slate-600 mt-1.5">Descubra o seu ID com @userinfobot — após vincular, o bot global liberta o menu para si.</p>
                </div>
              )}
              {ov?.bot?.can_write && (
              <div className="flex flex-wrap gap-3">
                <button data-testid="storage-save-btn" onClick={saveCfg} disabled={savingCfg} className="btn-neon px-5 py-3 rounded-xl text-sm">
                  {savingCfg ? <Spinner /> : "Salvar"}
                </button>
                <button data-testid="storage-reconnect-btn" onClick={reactivate} className="btn-ghost-dark px-5 py-3 rounded-xl text-sm text-slate-200 inline-flex items-center gap-2">
                  <RefreshCw size={14} /> Reconectar e atualizar
                </button>
              </div>
              )}
            </div>
          </GlassCard>
        </div>
      )}

      {tab === "accesses" && (
        <div className="space-y-5 anim-fade-up" data-testid="tab-content-accesses">
          <TutorialHint
            id="bot-accesses"
            title="Consumidores e acessos"
            steps={[
              "Crie uma credencial por cliente/dispositivo: usuário e senha são gerados automaticamente se vazios.",
              "Conexões simultâneas: use 0 para ilimitado ou indique a quantidade de conexões.",
              "No olho vê o utilizador, a senha, o link da playlist completa (get.php) e os links individuais de cada conteúdo.",
              "Pausar corta o acesso na hora; trocar senha invalida os links antigos.",
            ]}
          />
          <GlassCard className="p-6" data-testid="access-form-card">
            <h3 className="font-display text-white font-semibold text-sm mb-1.5">Consumidores e acessos</h3>
            <p className="text-xs text-slate-500 mb-5">Crie credenciais e defina quantos dispositivos podem reproduzir ao mesmo tempo.</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Nome do cliente *</label>
                <input data-testid="access-name-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Ex.: João — Sala principal" value={newAcc.name} onChange={(e) => setNewAcc({ ...newAcc, name: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Usuário da playlist</label>
                <input data-testid="access-username-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" placeholder="Gerado automaticamente se vazio" value={newAcc.username} onChange={(e) => setNewAcc({ ...newAcc, username: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Senha da playlist</label>
                <input data-testid="access-password-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm font-mono2" placeholder="Gerada automaticamente se vazia" value={newAcc.password} onChange={(e) => setNewAcc({ ...newAcc, password: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Acessos simultâneos</label>
                <input data-testid="access-limit-input" type="number" min="0" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={newAcc.max_concurrent} onChange={(e) => setNewAcc({ ...newAcc, max_concurrent: e.target.value })} />
                <p className="text-[10px] text-slate-600 mt-1.5">Use 0 para ilimitado.</p>
              </div>
            </div>
            <button data-testid="access-create-btn" onClick={createAccess} disabled={creatingAcc || !newAcc.name.trim()} className="btn-neon w-full mt-5 py-3.5 rounded-xl text-sm flex items-center justify-center gap-2">
              {creatingAcc ? <Spinner /> : <><Plus size={15} /> Criar acesso</>}
            </button>
          </GlassCard>

          {accesses === null ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : accesses.length === 0 ? (
            <EmptyState icon={Users} title="Nenhum acesso criado" subtitle="Crie a primeira credencial acima." testid="accesses-empty" />
          ) : (
            <div className="space-y-3" data-testid="accesses-list">
              {accesses.map((a) => (
                <GlassCard key={a.id} className="overflow-hidden" data-testid={`access-row-${a.id}`}>
                  <div className="p-4 flex flex-wrap items-center gap-3">
                    <KeyRound size={16} className="text-cyan-400 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white flex items-center gap-2 flex-wrap">
                        {a.name}
                        {a.watching && (
                          <span data-testid={`access-watching-${a.id}`} className="inline-flex items-center gap-1.5 text-[9px] font-mono2 uppercase tracking-widest text-emerald-300 border border-emerald-400/30 bg-emerald-400/10 rounded-full px-2 py-0.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                            {a.active_streams > 1 ? `A assistir · ${a.active_streams} conexões` : "A assistir agora"}
                          </span>
                        )}
                      </p>
                      <p className="text-[11px] text-slate-500 font-mono2 truncate">
                        {a.username} · {a.max_concurrent === 0 ? "acessos ilimitados" : `${a.max_concurrent} simultâneo(s)`} · {a.status === "active" ? "active" : "pausado"}{a.expires_at ? ` · até ${new Date(a.expires_at).toLocaleDateString("pt-PT")}` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-1.5 ml-auto">
                      <button data-testid={`access-limit-btn-${a.id}`} title="Alterar limite de acesso" onClick={() => setEditLimit({ ...editLimit, [a.id]: editLimit[a.id] === undefined ? String(a.max_concurrent) : undefined })} className="p-2 rounded-lg btn-ghost-dark text-slate-300 hover:text-white"><Users size={14} /></button>
                      <button data-testid={`access-creds-btn-${a.id}`} title="Visualizar credencial de link" onClick={() => viewCreds(a.id)} className="p-2 rounded-lg btn-ghost-dark text-slate-300 hover:text-white"><Eye size={14} /></button>
                      <button data-testid={`access-toggle-btn-${a.id}`} title={a.status === "active" ? "Pausar acesso" : "Retomar acesso"} onClick={() => toggleAccess(a.id)} className={`p-2 rounded-lg btn-ghost-dark ${a.status === "active" ? "text-amber-300" : "text-emerald-300"}`}>
                        {a.status === "active" ? <Pause size={14} /> : <Play size={14} />}
                      </button>
                      <button data-testid={`access-pass-btn-${a.id}`} title="Trocar senha" onClick={() => setEditPass({ ...editPass, [a.id]: editPass[a.id] === undefined ? "" : undefined })} className="p-2 rounded-lg btn-ghost-dark text-slate-300 hover:text-white"><RefreshCw size={14} /></button>
                      <button data-testid={`access-delete-btn-${a.id}`} title="Excluir" onClick={() => removeAccess(a.id)} className="p-2 rounded-lg text-rose-400 hover:bg-rose-500/10 border border-transparent hover:border-rose-500/30 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>

                  {editLimit[a.id] !== undefined && (
                    <div className="px-4 pb-4 flex gap-2 items-center" data-testid={`access-limit-editor-${a.id}`}>
                      <input type="number" min="0" className="input-dark rounded-lg px-3 py-2 text-sm w-32" value={editLimit[a.id]} onChange={(e) => setEditLimit({ ...editLimit, [a.id]: e.target.value })} />
                      <span className="text-[11px] text-slate-500">0 = ilimitado</span>
                      <button data-testid={`access-limit-save-${a.id}`} onClick={() => applyLimit(a.id)} className="btn-neon px-4 py-2 rounded-lg text-xs">Salvar limite</button>
                    </div>
                  )}

                  {editPass[a.id] !== undefined && (
                    <div className="px-4 pb-4 flex flex-wrap gap-2 items-center" data-testid={`access-pass-editor-${a.id}`}>
                      <input className="input-dark rounded-lg px-3 py-2 text-sm w-56 font-mono2" placeholder="Vazio = gerar aleatória" value={editPass[a.id]} onChange={(e) => setEditPass({ ...editPass, [a.id]: e.target.value })} />
                      <button data-testid={`access-pass-save-${a.id}`} onClick={() => applyPassword(a.id)} className="btn-violet px-4 py-2 rounded-lg text-xs">Trocar senha</button>
                    </div>
                  )}

                  {creds[a.id] && (
                    <div className="mx-4 mb-4 p-4 rounded-xl bg-slate-950/60 border border-slate-800/60 space-y-3 anim-fade-up" data-testid={`access-creds-panel-${a.id}`}>
                      <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Credenciais e links deste consumidor</p>
                      {[
                        ["Usuário", creds[a.id].username, `creds-user-${a.id}`],
                        ["Senha", creds[a.id].password, `creds-pass-${a.id}`],
                        ["Servidor Xtream (player_api)", creds[a.id].server_url, `creds-server-${a.id}`],
                        ["Playlist M3U8", creds[a.id].playlist_url, `creds-playlist-${a.id}`],
                      ].map(([label, value, tid]) => (
                        <div key={tid} className="flex items-center gap-2">
                          <div className="min-w-0 flex-1">
                            <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-600">{label}</p>
                            <code className="text-[11px] font-mono2 text-cyan-300/90 break-all" data-testid={tid}>{value}</code>
                          </div>
                          <button data-testid={`${tid}-copy`} onClick={() => copy(value)} className="p-1.5 rounded-lg btn-ghost-dark text-slate-400 hover:text-white shrink-0"><Copy size={13} /></button>
                        </div>
                      ))}
                      <a data-testid={`creds-playlist-download-${a.id}`} href={creds[a.id].playlist_url} download className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold border border-sky-400/30 text-sky-300 hover:bg-sky-400/10 transition-colors"><Download size={13} /> Baixar playlist (.m3u)</a>
                      <div className="pt-3 border-t border-slate-800/60">
                        <div className="flex items-center gap-2 mb-2.5">
                          <p className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500 flex-1">Links individuais deste acesso</p>
                          <input
                            className="input-dark rounded-lg px-2.5 py-1.5 text-[11px] w-40"
                            placeholder="Buscar título..."
                            value={linkQ[a.id] || ""}
                            onChange={(e) => setLinkQ((prev) => ({ ...prev, [a.id]: e.target.value }))}
                            onKeyDown={(e) => e.key === "Enter" && loadCredLinks(a.id, linkQ[a.id] || "")}
                            data-testid={`cred-links-search-${a.id}`}
                          />
                          <button onClick={() => loadCredLinks(a.id, linkQ[a.id] || "")} className="p-1.5 rounded-lg btn-ghost-dark text-slate-400 hover:text-white shrink-0" title="Buscar" data-testid={`cred-links-search-btn-${a.id}`}><Search size={13} /></button>
                        </div>
                        <div className="space-y-2.5 max-h-72 overflow-y-auto pr-1" data-testid={`cred-links-list-${a.id}`}>
                          {(linkItems[a.id]?.items || []).map((item) => {
                            const kindPath = item.media_type === "tv" ? "series" : "movie";
                            const link = `${window.location.origin}/api/xc/${kindPath}/${creds[a.id].username}/${creds[a.id].password}/${item.version_id}.mp4`;
                            const label = item.title + (item.season != null ? ` S${String(item.season).padStart(2, "0")}E${String(item.episode).padStart(2, "0")}` : "");
                            return (
                              <div key={item.version_id}>
                                <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-600">{label}</p>
                                <div className="flex items-center gap-2">
                                  <code className="text-[11px] font-mono2 text-cyan-300/90 break-all flex-1 min-w-0">{link}</code>
                                  <button data-testid={`cred-link-copy-${item.version_id}`} onClick={() => copy(link, "Link individual copiado!")} className="p-1.5 rounded-lg btn-ghost-dark text-slate-400 hover:text-white shrink-0"><Copy size={13} /></button>
                                  <a data-testid={`cred-link-download-${item.version_id}`} href={`${link}&download=1`} title="Baixar arquivo original" className="p-1.5 rounded-lg btn-ghost-dark text-sky-300 hover:text-sky-200 shrink-0"><Download size={13} /></a>
                                </div>
                              </div>
                            );
                          })}
                          {linkItems[a.id] === undefined && <p className="text-[11px] text-slate-600">Carregando…</p>}
                          {linkItems[a.id] !== undefined && !(linkItems[a.id]?.items || []).length && <p className="text-[11px] text-slate-600">Nenhum item encontrado — migre conteúdo ou ajuste a busca.</p>}
                        </div>
                        {(linkItems[a.id]?.total ?? 0) > (linkItems[a.id]?.items || []).length && (
                          <p className="text-[10px] text-slate-600 mt-2" data-testid={`cred-links-count-${a.id}`}>Mostrando {(linkItems[a.id]?.items || []).length} de {linkItems[a.id]?.total} — use a busca para filtrar.</p>
                        )}
                      </div>
                    </div>
                  )}
                </GlassCard>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "acervo" && (
        <div className="space-y-5 anim-fade-up" data-testid="tab-content-acervo">
          <TutorialHint
            id="bot-acervo"
            title="Acervo, séries e categorias"
            steps={[
              "As categorias vêm do group-title da playlist importada — e pode criar/renomear/excluir.",
              "Os contadores mostram filmes publicados, séries/episódios e espaço no storage.",
              "Em cada item: copiar link individual, editar (título, categoria, versão, qualidade) ou excluir.",
              "O link individual usa a primeira credencial ativa da aba Acessos.",
            ]}
          />
          {!acesso ? (
            <div className="flex justify-center py-12"><Spinner /></div>
          ) : (
            <>
              <GlassCard className="p-6" data-testid="categories-card">
                <h3 className="font-display text-white font-semibold text-sm mb-1.5">Categorias da playlist</h3>
                <p className="text-xs text-slate-500 mb-4">Organize filmes e episódios em categorias enviadas como group-title na playlist M3U.</p>
                <div className="flex flex-col sm:flex-row gap-3 mb-4">
                  <input data-testid="category-name-input" className="input-dark flex-1 rounded-xl px-4 py-2.5 text-sm" placeholder="Ex.: Filmes de ação" value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} />
                  <select data-testid="category-kind-select" className="input-dark rounded-xl px-3 py-2.5 text-sm" value={newCat.kind} onChange={(e) => setNewCat({ ...newCat, kind: e.target.value })}>
                    {KIND_OPTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <button data-testid="category-create-btn" onClick={createCategory} className="btn-neon px-5 py-2.5 rounded-xl text-sm shrink-0">Criar</button>
                </div>
                <div className="flex items-center justify-between gap-3 mb-3">
                  <span className="text-[10px] font-mono2 text-slate-500" data-testid="category-limit-info">Mostrando {Math.min(catLimit, acesso.categories.length)} de {acesso.categories.length}</span>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Mostrar</span>
                    <select data-testid="category-limit-select" className="input-dark rounded-lg px-2.5 py-1.5 text-xs" value={catLimit} onChange={(e) => setCatLimit(Number(e.target.value))}>
                      {[10, 50, 100, 250, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                </div>
                <div className="space-y-2" data-testid="categories-list">
                  {acesso.categories.length === 0 && <p className="text-xs text-slate-600">Nenhuma categoria ainda — aparecem quando migrar uma playlist.</p>}
                  {acesso.categories.slice(0, catLimit).map((c) => (
                    <div key={`${c.kind}-${c.name}`} className="flex items-center gap-3 p-3 rounded-xl bg-slate-950/50 border border-slate-800/60" data-testid={`category-row-${c.name}`}>
                      <span className="text-[9px] font-mono2 uppercase tracking-wider px-1.5 py-0.5 rounded border border-slate-700 text-slate-400">{c.kind === "tv" ? "SÉRIE" : "FILME"}</span>
                      {renaming === c.name ? (
                        <>
                          <input className="input-dark rounded-lg px-3 py-1.5 text-sm flex-1" value={renameVal} onChange={(e) => setRenameVal(e.target.value)} data-testid={`category-rename-input-${c.name}`} />
                          <button onClick={() => renameCategory(c.name)} className="btn-neon px-3 py-1.5 rounded-lg text-xs" data-testid={`category-rename-save-${c.name}`}>Salvar</button>
                        </>
                      ) : (
                        <span className="text-sm text-white font-medium flex-1 truncate">⚡ {c.name}</span>
                      )}
                      <span className="text-[11px] font-mono2 text-slate-500">{c.count}</span>
                      <button data-testid={`category-edit-${c.name}`} onClick={() => { setRenaming(c.name); setRenameVal(c.name); }} className="p-1.5 rounded-lg text-slate-500 hover:text-cyan-300 transition-colors"><Pencil size={13} /></button>
                      <button data-testid={`category-delete-${c.name}`} onClick={() => deleteCategory(c.name)} className="p-1.5 rounded-lg text-slate-500 hover:text-rose-400 transition-colors"><Trash2 size={13} /></button>
                    </div>
                  ))}
                </div>
              </GlassCard>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="acervo-stats">
                {[
                  { label: "Filmes publicados", value: acesso.stats.filmes, icon: Film, accent: "text-cyan-400" },
                  { label: "Séries / Episódios", value: `${acesso.stats.series} / ${acesso.stats.episodios}`, icon: Tv, accent: "text-purple-400" },
                  { label: "Storage publicado", value: fmtBytes(acesso.stats.storage_bytes), icon: Database, accent: "text-emerald-400" },
                  { label: "Arquivados", value: acesso.stats.arquivados, icon: Archive, accent: "text-amber-400" },
                ].map((c) => (
                  <GlassCard key={c.label} className="p-4 glass-hover">
                    <c.icon size={16} className={c.accent} />
                    <p className="font-display text-lg font-bold text-white mt-2">{c.value}</p>
                    <p className="text-[9px] font-mono2 uppercase tracking-widest text-slate-500 mt-1">{c.label}</p>
                  </GlassCard>
                ))}
              </div>

              {acesso.stats.arquivados > 0 && (
                <div data-testid="trash-quota-banner" className="glass rounded-xl px-4 py-3 flex flex-wrap items-center gap-3 border-amber-400/30 bg-amber-400/5">
                  <Trash2 size={16} className="text-amber-400 shrink-0" />
                  <p className="text-xs text-amber-200/90 flex-1 min-w-[200px]">
                    <span className="font-semibold text-amber-300">{acesso.stats.arquivados} {acesso.stats.arquivados === 1 ? "item" : "itens"} na lixeira</span> ocupando {fmtBytes(acesso.stats.trash_bytes || 0)} — esvazie para liberar sua cota.
                  </p>
                  {filters.archived !== "true" && (
                    <button data-testid="trash-quota-view-btn" onClick={() => applyFilters({ archived: "true" })} className="px-3.5 py-2 rounded-lg text-xs font-semibold border border-amber-400/30 text-amber-300 hover:bg-amber-400/10 transition-colors">Ver lixeira</button>
                  )}
                  <button data-testid="trash-quota-empty-btn" onClick={() => setConfirmState({ title: "Esvaziar lixeira", text: "Isso exclui DEFINITIVAMENTE todos os itens arquivados, apagando os arquivos do storage e liberando o espaço. Não tem volta.", okLabel: "Esvaziar tudo", fn: emptyTrash })} className="px-3.5 py-2 rounded-lg text-xs font-semibold border border-rose-400/30 text-rose-300 hover:bg-rose-400/10 transition-colors">Esvaziar agora</button>
                </div>
              )}

              <GlassCard className="p-5" data-testid="acervo-filters-card">
                <div className="flex flex-wrap items-center gap-2.5">
                  {[["false", "Publicados"], ["true", "Arquivados"]].map(([v, l]) => (
                    <button key={v} data-testid={`filter-archived-${v}`} onClick={() => applyFilters({ archived: v })} className={`px-3.5 py-2 rounded-lg text-xs font-semibold border transition-colors ${filters.archived === v ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/30" : "text-slate-400 border-slate-800 hover:text-white"}`}>{l}</button>
                  ))}
                  {[["", "Todos"], ["movie", "Filmes"], ["tv", "Séries"]].map(([v, l]) => (
                    <button key={v} data-testid={`filter-kind-${v || "all"}`} onClick={() => applyFilters({ kind: v })} className={`px-3.5 py-2 rounded-lg text-xs font-semibold border transition-colors ${filters.kind === v ? "bg-purple-400/15 text-purple-300 border-purple-400/30" : "text-slate-400 border-slate-800 hover:text-white"}`}>{l}</button>
                  ))}
                  <div className="relative flex-1 min-w-[180px]">
                    <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
                    <input data-testid="acervo-search-input" className="input-dark w-full rounded-xl pl-10 pr-4 py-2.5 text-sm" placeholder="Buscar título, arquivo ou categoria" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && applyFilters({})} onBlur={() => applyFilters({})} />
                  </div>
                  <select data-testid="acervo-category-select" className="input-dark rounded-xl px-3 py-2.5 text-sm" value={filters.category} onChange={(e) => applyFilters({ category: e.target.value })}>
                    <option value="">Todas as categorias</option>
                    {acesso.categories.map((c) => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                  <button data-testid="bulk-filter-open-btn" onClick={() => setBulkFilterOpen(true)} className="px-3.5 py-2 rounded-lg text-xs font-semibold border border-rose-400/30 text-rose-300 hover:bg-rose-400/10 transition-colors">Excluir por filtro</button>
                  {filters.archived === "true" && acesso.total > 0 && (
                    <button data-testid="trash-empty-btn" onClick={() => setConfirmState({ title: "Esvaziar lixeira", text: "Isso exclui DEFINITIVAMENTE todos os itens arquivados, apagando os arquivos do storage e liberando o espaço. Não tem volta.", okLabel: "Esvaziar tudo", fn: emptyTrash })} className="px-3.5 py-2 rounded-lg text-xs font-semibold border border-rose-400/30 text-rose-300 hover:bg-rose-400/10 transition-colors">Esvaziar lixeira</button>
                  )}
                  <span className="text-[10px] font-mono2 text-slate-500" data-testid="acervo-count">{acesso.total} resultado(s)</span>
                </div>
              </GlassCard>

              {selected.size > 0 && (
                <div className="glass rounded-xl px-4 py-3 flex flex-wrap items-center gap-3" data-testid="bulk-bar">
                  <span className="text-xs text-white font-semibold" data-testid="bulk-count">{selected.size} selecionado(s)</span>
                  {filters.archived === "true" ? (
                    <button data-testid="bulk-restore-btn" disabled={bulkBusy} onClick={() => runBulk("restore", [...selected])} className="px-3.5 py-2 rounded-lg text-xs font-semibold border border-emerald-400/30 text-emerald-300 hover:bg-emerald-400/10 transition-colors">Restaurar</button>
                  ) : (
                    <button data-testid="bulk-archive-btn" disabled={bulkBusy} onClick={() => runBulk("archive", [...selected])} className="px-3.5 py-2 rounded-lg text-xs font-semibold border border-amber-400/30 text-amber-300 hover:bg-amber-400/10 transition-colors">Mover p/ lixeira</button>
                  )}
                  <button
                    data-testid="bulk-delete-btn"
                    disabled={bulkBusy}
                    onClick={() => setConfirmState({ title: "Excluir definitivamente", text: `Excluir ${selected.size} item(ns) para sempre? Os arquivos serão apagados do storage e o espaço liberado. Não tem volta.`, okLabel: "Excluir tudo", fn: () => runBulk("delete", [...selected]) })}
                    className="px-3.5 py-2 rounded-lg text-xs font-semibold border border-rose-400/30 text-rose-300 hover:bg-rose-400/10 transition-colors"
                  >Excluir definitivamente</button>
                  <button data-testid="bulk-clear-btn" onClick={() => setSelected(new Set())} className="text-xs text-slate-500 hover:text-slate-300">limpar seleção</button>
                </div>
              )}
              {acesso.items.length > 0 && (
                <div className="flex items-center gap-2 px-1">
                  <input data-testid="acervo-select-all" type="checkbox" checked={allSelected} onChange={toggleSelectAll} className="w-4 h-4 accent-cyan-400" />
                  <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Selecionar todos desta página</span>
                </div>
              )}
              {acesso.items.length === 0 ? (
                <EmptyState icon={Layers} title="Acervo vazio" subtitle="Migre uma playlist ou envie conteúdo pelo bot para preencher este acervo." testid="acervo-empty" />
              ) : (
                <div className="space-y-2" data-testid="acervo-list">
                  {acesso.items.map((item) => (
                    <GlassCard key={item.version_id} className={`px-3 py-2.5 flex flex-wrap items-center gap-3 ${selected.has(item.version_id) ? "border-cyan-400/40" : ""}`} data-testid={`acervo-item-${item.version_id}`}>
                      <input data-testid={`acervo-select-${item.version_id}`} type="checkbox" checked={selected.has(item.version_id)} onChange={() => toggleSelect(item.version_id)} className="w-4 h-4 accent-cyan-400 shrink-0" />
                      <div className="w-8 h-8 rounded-lg bg-slate-800 flex items-center justify-center shrink-0 border border-slate-800/60" data-testid={`acervo-icon-${item.version_id}`}>
                        {item.media_type === "tv" ? <Tv size={13} className="text-purple-400" /> : <Film size={13} className="text-cyan-400" />}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-white truncate">
                          {item.title}
                          {item.season != null && <span className="text-slate-400 text-[11px] ml-1.5 font-mono2">S{String(item.season).padStart(2, "0")}E{String(item.episode).padStart(2, "0")}</span>}
                        </p>
                        <p className="text-[10px] text-slate-500 truncate font-mono2" data-testid={`acervo-meta-${item.version_id}`}>
                          {item.media_type === "tv" ? "Série" : "Filme"}{item.year ? ` · ${item.year}` : ""} · {item.category || "Sem categoria"} · {fmtBytes(item.size_bytes)} · {item.archived ? "na lixeira" : "publicado"}
                        </p>
                      </div>
                      <VersionBadge version={item.version} />
                      <QualityBadge quality={item.quality} />
                      <div className="flex items-center gap-1.5">
                        <button
                          data-testid={`acervo-play-${item.version_id}`}
                          title="Assistir"
                          onClick={() => {
                            const link = itemLink(item);
                            if (!link) { toast.error("Crie um acesso ativo na aba Acessos para assistir"); return; }
                            setPlaying(item);
                          }}
                          className="p-2 rounded-lg btn-ghost-dark text-emerald-400 hover:text-emerald-300"
                        ><Play size={13} /></button>
                        <button data-testid={`acervo-edit-${item.version_id}`} title="Editar" onClick={() => openEdit(item)} className="p-2 rounded-lg btn-ghost-dark text-slate-300 hover:text-white"><Pencil size={13} /></button>
                        <button
                          data-testid={`acervo-copy-${item.version_id}`}
                          title="Copiar link individual"
                          onClick={() => {
                            const link = itemLink(item);
                            if (!link) { toast.error("Crie um acesso ativo na aba Acessos para gerar links"); return; }
                            copy(link, "Link individual copiado!");
                          }}
                          className="p-2 rounded-lg btn-ghost-dark text-slate-300 hover:text-white"
                        ><Copy size={13} /></button>
                        <button
                          data-testid={`acervo-download-${item.version_id}`}
                          title="Baixar arquivo original"
                          onClick={() => {
                            const link = itemLink(item);
                            if (!link) { toast.error("Crie um acesso ativo na aba Acessos para baixar"); return; }
                            window.open(`${link}&download=1`, "_blank");
                          }}
                          className="p-2 rounded-lg btn-ghost-dark text-sky-300 hover:text-sky-200"
                        ><Download size={13} /></button>
                        <button data-testid={`acervo-delete-${item.version_id}`} title="Excluir" onClick={() => removeVersion(item.version_id)} className="p-2 rounded-lg text-rose-400 hover:bg-rose-500/10 border border-transparent hover:border-rose-500/30 transition-colors"><Trash2 size={13} /></button>
                      </div>
                    </GlassCard>
                  ))}
                </div>
              )}
              {acesso.total > 0 && (
                <div className="flex flex-wrap items-center gap-3 justify-between" data-testid="acervo-pagination">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono2 uppercase tracking-widest text-slate-500">Por página</span>
                    <select data-testid="acervo-page-size-select" className="input-dark rounded-lg px-2.5 py-1.5 text-xs" value={pageSize} onChange={(e) => changePageSize(Number(e.target.value))}>
                      {[10, 25, 50, 100, 250, 500, 1000].map((n) => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-mono2 text-slate-500" data-testid="acervo-page-info">
                      {page * pageSize + 1}–{Math.min(acesso.total, (page + 1) * pageSize)} de {acesso.total}
                    </span>
                    <button data-testid="acervo-prev-page" disabled={page === 0} onClick={() => goPage(-1)} className="btn-ghost-dark px-3 py-1.5 rounded-lg text-xs text-slate-300 disabled:opacity-40">← Ant.</button>
                    <button data-testid="acervo-next-page" disabled={(page + 1) * pageSize >= acesso.total} onClick={() => goPage(1)} className="btn-ghost-dark px-3 py-1.5 rounded-lg text-xs text-slate-300 disabled:opacity-40">Próx. →</button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      <Modal open={!!editItem} onClose={() => setEditItem(null)} title="Editar item do acervo" testid="edit-item-modal">
        {editItem && (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Título</label>
                <input data-testid="edit-title-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={editForm.title || ""} onChange={(e) => setEditForm({ ...editForm, title: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Categoria</label>
                <input data-testid="edit-category-input" className="input-dark w-full rounded-xl px-4 py-3 text-sm" placeholder="Vazio = sem categoria" value={editForm.category || ""} onChange={(e) => setEditForm({ ...editForm, category: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Ano</label>
                <input data-testid="edit-year-input" type="number" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={editForm.year || ""} onChange={(e) => setEditForm({ ...editForm, year: e.target.value })} />
              </div>
              <div>
                <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Qualidade</label>
                <select data-testid="edit-quality-select" className="input-dark w-full rounded-xl px-3 py-3 text-sm" value={editForm.quality} onChange={(e) => setEditForm({ ...editForm, quality: e.target.value })}>
                  {["CAM", "TS", "SD", "HD", "FHD", "UHD4K"].map((qv) => <option key={qv} value={qv}>{qv}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Áudio</label>
                <select data-testid="edit-version-select" className="input-dark w-full rounded-xl px-3 py-3 text-sm" value={editForm.version} onChange={(e) => setEditForm({ ...editForm, version: e.target.value })}>
                  {["dublado", "legendado", "dual", "nacional"].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            </div>
            <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
              <input data-testid="edit-archived-check" type="checkbox" checked={!!editForm.archived} onChange={(e) => setEditForm({ ...editForm, archived: e.target.checked })} className="w-4 h-4 accent-cyan-400" />
              Arquivado (some da playlist pública)
            </label>
            <button data-testid="edit-save-btn" onClick={saveEdit} className="btn-neon w-full py-3 rounded-xl text-sm">Salvar alterações</button>
          </div>
        )}
      </Modal>

      <Modal open={!!playing} onClose={() => setPlaying(null)} title={playing ? `Assistir — ${playing.title}${playing.season != null ? ` S${String(playing.season).padStart(2, "0")}E${String(playing.episode).padStart(2, "0")}` : ""}` : "Assistir"} testid="player-modal">
        {playing && (
          <div>
            <video
              key={playing.version_id}
              controls
              autoPlay
              src={itemLink(playing) || ""}
              className="w-full rounded-xl bg-black aspect-video"
              data-testid="acervo-player"
            />
            <p className="text-[11px] text-slate-500 mt-3">Streaming direto do acervo — o consumo de banda desconta do saldo de créditos da operação.</p>
          </div>
        )}
      </Modal>

      <Modal open={bulkFilterOpen} onClose={() => setBulkFilterOpen(false)} title="Excluir por filtro" testid="bulk-filter-modal">
        <div className="space-y-4">
          <p className="text-xs text-slate-400 leading-relaxed">A ação vale para TODOS os itens publicados que baterem no filtro — não só os da página atual. Combine os campos como quiser.</p>
          <div>
            <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Categoria</label>
            <select data-testid="bulk-filter-category" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={bulkForm.category} onChange={(e) => setBulkForm({ ...bulkForm, category: e.target.value })}>
              <option value="">Todas as categorias</option>
              {(acesso?.categories || []).map((c) => <option key={c.name} value={c.name}>{c.name} ({c.count})</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Acima de (MB)</label>
              <input data-testid="bulk-filter-min-size" type="number" min="0" step="100" placeholder="Ex: 500" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={bulkForm.min_size_mb} onChange={(e) => setBulkForm({ ...bulkForm, min_size_mb: e.target.value })} />
            </div>
            <div>
              <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Tipo</label>
              <select data-testid="bulk-filter-kind" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={bulkForm.kind} onChange={(e) => setBulkForm({ ...bulkForm, kind: e.target.value })}>
                <option value="">Filmes e séries</option>
                <option value="movie">Só filmes</option>
                <option value="tv">Só séries</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-[11px] font-mono2 uppercase tracking-widest text-slate-500 block mb-2">Ação</label>
            <select data-testid="bulk-filter-action" className="input-dark w-full rounded-xl px-4 py-3 text-sm" value={bulkForm.action} onChange={(e) => setBulkForm({ ...bulkForm, action: e.target.value })}>
              <option value="archive">Mover para a lixeira (recuperável)</option>
              <option value="delete">Excluir definitivamente (libera espaço)</option>
            </select>
          </div>
          <button data-testid="bulk-filter-apply-btn" onClick={runBulkFilter} disabled={bulkBusy} className="btn-neon w-full py-3 rounded-xl text-sm flex items-center justify-center gap-2">
            {bulkBusy ? <Spinner /> : "Aplicar no filtro"}
          </button>
        </div>
      </Modal>

      <Modal open={!!confirmState} onClose={() => setConfirmState(null)} title={confirmState?.title || "Confirmar"} testid="confirm-modal">
        {confirmState && (
          <div className="space-y-4">
            <p className="text-sm text-slate-300 leading-relaxed" data-testid="confirm-modal-text">{confirmState.text}</p>
            <div className="flex gap-3">
              <button data-testid="confirm-modal-ok" disabled={bulkBusy} onClick={confirmState.fn} className="flex-1 py-3 rounded-xl text-sm font-semibold bg-rose-500/20 border border-rose-400/40 text-rose-300 hover:bg-rose-500/30 transition-colors flex items-center justify-center gap-2">
                {bulkBusy ? <Spinner /> : confirmState.okLabel}
              </button>
              <button data-testid="confirm-modal-cancel" onClick={() => setConfirmState(null)} className="btn-ghost-dark px-5 py-3 rounded-xl text-sm text-slate-300">Voltar</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
